import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const syntaxComparisonCode = `//  Static Import (Synchronous/Blocking)
import { heavilyLoaded } from './heavy-module.js';
import { KeyFacts, ProTip } from '../../components/LessonUI';

// Dynamic Import (Asynchronous/Non-blocking)
async function initializeFeature() {
    try {
        const { heavilyLoaded } = await import('./heavy-module.js');
        heavilyLoaded();
    } catch (err) {
        console.error('Module failed to load', err);
    }
}`;

const pragmaticLoaderCode = `// --- The Pragmatic Loader Pattern ---
const Loader = {
    _cache: new Map(),

    async load(modulePath) {
        if (this._cache.has(modulePath)) return this._cache.get(modulePath);

        const promise = import(modulePath).catch(err => {
            this._cache.delete(modulePath); // Evict failure from cache
            throw err;
        });

        this._cache.set(modulePath, promise);
        return promise;
    }
};

// Usage in UI
button.onclick = () => Loader.load('/modules/chart.js');`;

const predictiveLoadingCode = `// --- Predictive Prefetching ---
const link = document.querySelector('#admin-link');

link.addEventListener('mouseenter', () => {
    // Start download while user hovers (usually ~200ms before click)
    import('./pages/AdminDashboard.js');
}, { once: true });`;

const JSDynamicImports = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Modular Architecture</div>
                <h1>Dynamic Imports & Code Splitting</h1>
                <p className="hero-lead">
                    Modern web performance is defined by what you *don't* load.
                    Master the `import()` expression to implement lazy-loading,
                    reduce bundle sizes, and create ultra-responsive interfaces.
                </p>
            </div>

            <div className="content-section">
                <h2>The Cost of Static Cargo</h2>
                <p>
                    Standard `import` statements at the top of your files are
                    <strong>Static</strong>. This means even if a user never
                    opens your "Settings" panel, they still download the 100kb
                    of code required to power it. Dynamic imports allow you to
                    break your application into logical "chunks" that are fetched
                    only upon request.
                </p>
                <CodeBlock code={syntaxComparisonCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Pragmatic Loader Pattern</h2>
                <p>
                    Directly calling `import()` everywhere can lead to redundant
                    network requests or messy error handling. A <strong>Pragmatic Loader</strong>
                    wraps the native API to provide caching, retry logic, and
                    centralized error reporting.
                </p>
                <CodeBlock code={pragmaticLoaderCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Predictive Loading</h2>
                <p>
                    Dynamic loading often introduces a "loading lag." To eliminate
                    this, we use <strong>Predictive Prefetching</strong>. By listening
                    for high-intent signals (like a mouse hover over a link), we
                    can start the download before the user even clicks the button.
                </p>
                <CodeBlock code={predictiveLoadingCode} language="javascript" />
            </div>

            <ProTip><p>
                    Standard imports have the highest priority in the browser's
                    network queue. Dynamic imports have a lower priority
                    by default. You can influence this behavior in tools like
                    Webpack using magic comments like <code>/* webpackPreload: true */</code>.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Route-Based:** Split code by URL (e.g., /home, /admin).</li>
                    <li>**Interaction-Based:** Load heavy libraries (Charts, Editors) only when active.</li>
                    <li>**Polyfills:** Only load compatibility code for older browsers.</li>
                    <li>**Error Boundaries:** Always wrap dynamic imports in try/catch blocks.</li>
                    <li> **Micro-Splitting:** Avoid splitting files smaller than 5kb; the HTTP overhead outweighs the benefit.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDynamicImports;


